import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, XCircle, Plus, Loader2, AlertTriangle, Repeat } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["Low", "Medium", "High"];
const STATUSES = ["Open", "In Progress", "Solved", "Recurring"];
const statusColor = { Open: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300", "In Progress": "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300", Solved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300", Recurring: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300" };
const today = new Date().toISOString().slice(0, 10);

export default function DailyConnect() {
  const { departments, centers } = useApp();
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [checkins, setCheckins] = useState([]);
  const [issues, setIssues] = useState([]);
  const [library, setLibrary] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({});
  const [checkinForm, setCheckinForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [cs, is, lib, rec] = await Promise.all([
      api.get(`/connect/checkin-status?date=${today}`), api.get("/issues"),
      api.get("/problem-library"), api.get("/issues-recurring"),
    ]);
    setCheckins(cs.data); setIssues(is.data); setLibrary(lib.data); setRecurring(rec.data);
  };
  useEffect(() => { load(); }, []);

  const saveIssue = async () => {
    setSaving(true);
    try {
      const d = departments.find((x) => x.id === issueForm.department_id);
      const c = centers.find((x) => x.id === issueForm.center_id);
      const payload = { ...issueForm, department_name: d?.name, center_code: c?.code };
      if (issueForm.id) await api.put(`/issues/${issueForm.id}`, payload);
      else await api.post("/issues", payload);
      toast.success("Issue saved"); setIssueOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveCheckin = async () => {
    setSaving(true);
    try {
      const d = departments.find((x) => x.id === checkinForm.department_id);
      await api.post("/checkins", { ...checkinForm, department_name: d?.name, date: today });
      toast.success("Check-in submitted"); setCheckinOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const libByDept = {};
  library.forEach((p) => { (libByDept[p.department] ||= []).push(p); });

  return (
    <div data-testid="daily-connect-page">
      <PageHeader eyebrow="Daily Operations" title="Daily Connect & Issue Tracker"
        subtitle="2-minute check-ins, issue logging with system solutions, and recurring-problem detection." />

      <Tabs defaultValue="checkins">
        <TabsList>
          <TabsTrigger value="checkins" data-testid="tab-checkins">Daily Check-ins</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issue Log</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library">Problem → Solution Library</TabsTrigger>
        </TabsList>

        <TabsContent value="checkins" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setCheckinForm({ department_id: departments[0]?.id, center_id: centers[0]?.id }); setCheckinOpen(true); }} data-testid="add-checkin-button" style={{ display: canWrite ? undefined : "none" }}>
              <Plus className="h-4 w-4 mr-1" /> Submit Check-in
            </Button>
          </div>
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-4">Today · {today}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {checkins.map((c) => (
                <div key={c.department_id} className="rounded-xl border border-border p-4 flex items-center gap-3" data-testid={`checkin-${c.department_id}`}>
                  {c.checked_in ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-rose-400" />}
                  <div><p className="font-medium text-sm">{c.department_name}</p>
                    <p className="text-xs text-muted-foreground">{c.checked_in ? "Checked in" : "Pending"}</p></div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="issues" className="mt-4 space-y-4">
          {recurring.length > 0 && (
            <Card className="p-4 border-violet-300 bg-violet-50 dark:bg-violet-950/30">
              <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
                <Repeat className="h-4 w-4" />
                <p className="text-sm font-medium">Recurring problems detected: {recurring.map((r) => `${r.category} (${r.count}×)`).join(", ")}</p>
              </div>
            </Card>
          )}
          <div className="flex justify-end">
            <Button onClick={() => { setIssueForm({ department_id: departments[0]?.id, center_id: centers[0]?.id, priority: "Medium", status: "Open" }); setIssueOpen(true); }} data-testid="add-issue-button" style={{ display: canWrite ? undefined : "none" }}>
              <Plus className="h-4 w-4 mr-1" /> Log Issue
            </Button>
          </div>
          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Problem</TableHead><TableHead>Dept</TableHead><TableHead>System Solution</TableHead>
                <TableHead>Owner</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {issues.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => { setIssueForm(i); setIssueOpen(true); }} data-testid={`issue-row-${i.id}`}>
                    <TableCell className="font-medium max-w-[220px]">{i.problem}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.department_name}</TableCell>
                    <TableCell className="text-xs max-w-[220px]">{i.system_solution}</TableCell>
                    <TableCell className="text-xs">{i.owner}</TableCell>
                    <TableCell><Badge variant="outline">{i.priority}</Badge></TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusColor[i.status]}`}>{i.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="library" className="mt-4 space-y-4">
          {Object.entries(libByDept).map(([dept, items]) => (
            <Card key={dept} className="p-5">
              <h3 className="font-display font-semibold mb-3">{dept}</h3>
              <div className="space-y-2">
                {items.map((p) => (
                  <div key={p.id} className="flex items-start gap-3 text-sm" data-testid={`lib-${p.id}`}>
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="flex-1"><span className="font-medium">{p.problem}</span>
                      <span className="text-muted-foreground"> → {p.system_solution}</span></p>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent data-testid="issue-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{issueForm.id ? "Edit Issue" : "Log Issue"}</DialogTitle>
            <DialogDescription>Capture the problem, root cause and the system-based solution.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label className="text-xs">Problem</Label><Input value={issueForm.problem || ""} onChange={(e) => setIssueForm({ ...issueForm, problem: e.target.value })} data-testid="issue-problem-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Department</Label>
                <Select value={issueForm.department_id} onValueChange={(v) => setIssueForm({ ...issueForm, department_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Center</Label>
                <Select value={issueForm.center_id} onValueChange={(v) => setIssueForm({ ...issueForm, center_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Root cause</Label><Input value={issueForm.root_cause || ""} onChange={(e) => setIssueForm({ ...issueForm, root_cause: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">System solution</Label><Textarea value={issueForm.system_solution || ""} onChange={(e) => setIssueForm({ ...issueForm, system_solution: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Owner</Label><Input value={issueForm.owner || ""} onChange={(e) => setIssueForm({ ...issueForm, owner: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Category</Label><Input value={issueForm.category || ""} onChange={(e) => setIssueForm({ ...issueForm, category: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
                <Select value={issueForm.priority} onValueChange={(v) => setIssueForm({ ...issueForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Status</Label>
                <Select value={issueForm.status} onValueChange={(v) => setIssueForm({ ...issueForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button>
            <Button onClick={saveIssue} disabled={saving || !issueForm.problem} data-testid="save-issue-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check-in dialog */}
      <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
        <DialogContent data-testid="checkin-dialog">
          <DialogHeader><DialogTitle>Daily Check-in</DialogTitle>
            <DialogDescription>Yesterday's numbers, today's plan, blockers and help needed.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Department</Label>
                <Select value={checkinForm.department_id} onValueChange={(v) => setCheckinForm({ ...checkinForm, department_id: v })}>
                  <SelectTrigger data-testid="checkin-dept-select"><SelectValue /></SelectTrigger><SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Center</Label>
                <Select value={checkinForm.center_id} onValueChange={(v) => setCheckinForm({ ...checkinForm, center_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Yesterday's numbers</Label><Input value={checkinForm.yesterday || ""} onChange={(e) => setCheckinForm({ ...checkinForm, yesterday: e.target.value })} data-testid="checkin-yesterday-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Today's plan</Label><Input value={checkinForm.today_plan || ""} onChange={(e) => setCheckinForm({ ...checkinForm, today_plan: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Blockers</Label><Input value={checkinForm.blockers || ""} onChange={(e) => setCheckinForm({ ...checkinForm, blockers: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Help needed from CEO</Label><Input value={checkinForm.help_needed || ""} onChange={(e) => setCheckinForm({ ...checkinForm, help_needed: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCheckinOpen(false)}>Cancel</Button>
            <Button onClick={saveCheckin} disabled={saving} data-testid="save-checkin-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
